import { type Href } from 'expo-router'
import { sample } from 'lodash'
import { useEffect } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'

import { type UserRedirectScreenQuery } from '@/__generated__/UserRedirectScreenQuery.graphql'
import { SplashScreen } from '@/components'
import { usePreloadableRouter } from '@/hooks'
import { consumePendingRoute } from '@/lib'

const messages = [
  'Making love accessible',
  'Love without barriers',
  'Access meets affection',
  'Come as you are',
  'Flirting made easy',
  'Holding space for love',
  'All kinds of love welcome',
  'No barriers, just sparks',
  'Real love, real access',
  'Rolling into your heart',
  'Accessible by design',
  'Space for every story',
  'Love that sees you',
  'Built for every body',
  'Access is romantic',
  'Chairs welcome, hearts open',
  'Love that fits you',
  'All feelings valid',
  'Kindness is hot',
  'Crushing it accessibly'
]

export const UserRedirectScreen = () => {
  const router = usePreloadableRouter()
  const queryData = useLazyLoadQuery<UserRedirectScreenQuery>(
    graphql`
      query UserRedirectScreenQuery {
        currentUser {
          onboardedAt
          status
          rowId
        }
      }
    `,
    {}
  )

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (queryData?.currentUser?.rowId) {
        if (queryData.currentUser.status === 'INACTIVE' || queryData.currentUser.status === 'SCHEDULED_FOR_DELETION') {
          return router.replace('/account-inactive', {
            fetchPolicy: 'network-only',
            queryName: 'AccountInactiveScreenQuery'
          })
        }

        if (queryData.currentUser.status === 'BANNED' || queryData.currentUser.status === 'APPEAL') {
          return router.replace('/banned', {
            fetchPolicy: 'network-only',
            queryName: 'BannedScreenQuery'
          })
        }

        if (!queryData.currentUser.onboardedAt) {
          return router.replace('/onboarding/terms', {
            fetchPolicy: 'network-only',
            queryName: 'OnboardingTermsScreenQuery'
          })
        }

        // a deep link that arrived signed-out parked its route here on the way to sign-in. no preload for
        // it -- ScreenLoader falls back to loading the query itself, same as any hard reload. DBY-467
        const pendingRoute = consumePendingRoute()

        if (pendingRoute) {
          return router.replace(pendingRoute as Href)
        }

        router.replace('/search', {
          fetchPolicy: 'network-only',
          queryName: 'SearchScreenQuery'
        })
      } else {
        router.replace('/sign-in')
      }
    }, 2_000)

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [queryData?.currentUser?.onboardedAt, queryData?.currentUser?.rowId, queryData?.currentUser?.status, router])

  return <SplashScreen message={sample(messages)} />
}
